import React from 'react'
import styled, { css } from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { useAppContext } from '~/app-context'
import {
    type VendorDisplayLogicDependencies,
    VendorDisplayLogic,
} from './logic'

export interface VendorDisplayProps extends VendorDisplayLogicDependencies {
    vendorName: string
    showRefreshButton?: boolean
    hideEmail?: boolean
    hideAvatar?: boolean
}

export function VendorDisplay(props: VendorDisplayProps) {
    const appContext = useAppContext()
    const { logic, state } = useLogic(VendorDisplayLogic, {
        vendorEmail: props.vendorEmail,
        ...appContext,
    })

    const handleRefreshFavicon = () => {
        logic.refreshFavicon()
    }

    return (
        <Container>
            {!props.hideAvatar && (
                <FaviconContainer hasFavicon={!!state.faviconUrl}>
                    {state.isLoadingFavicon ? (
                        <LoadingIndicator />
                    ) : state.faviconUrl ? (
                        <FaviconImage
                            src={state.faviconUrl}
                            alt={`${props.vendorName} favicon`}
                            onError={(e) => {
                                // Hide the image if it fails to load
                                e.currentTarget.style.display = 'none'
                            }}
                        />
                    ) : (
                        <FallbackIcon>
                            {props.vendorName.charAt(0).toUpperCase()}
                        </FallbackIcon>
                    )}
                </FaviconContainer>
            )}
            <VendorInfo>
                <VendorName>{props.vendorName}</VendorName>
                {!props.hideEmail && (
                    <VendorDomain>{props.vendorEmail}</VendorDomain>
                )}
            </VendorInfo>
            {props.showRefreshButton && (
                <RefreshButton
                    onClick={handleRefreshFavicon}
                    disabled={state.isLoadingFavicon}
                    title="Refresh favicon"
                >
                    ↻
                </RefreshButton>
            )}
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    overflow: hidden;
`

const FaviconContainer = styled.div<{ hasFavicon: boolean }>`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.background.bg3};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
    ${({ hasFavicon }) =>
        hasFavicon &&
        css`
            background-color: ${({ theme }) => theme.colors.background.bg6};
        `}
`

const FaviconImage = styled.img`
    width: 100%;
    height: 100%;
    border-radius: 40px;
    object-fit: contain;
`

const FallbackIcon = styled.div`
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
`

const LoadingIndicator = styled.div`
    width: 16px;
    height: 16px;
    border: 2px solid ${({ theme }) => theme.colors.border.primary};
    border-top: 2px solid ${({ theme }) => theme.colors.success};
    border-radius: 50%;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`

const VendorInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const VendorName = styled.div`
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: 14px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const VendorDomain = styled.div`
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const RefreshButton = styled.button`
    background: none;
    border: 1px solid ${({ theme }) => theme.colors.border.primary};
    border-radius: 4px;
    color: ${({ theme }) => theme.colors.text.secondary};
    cursor: pointer;
    padding: 4px 8px;
    font-size: 16px;
    min-width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: ${({ theme }) => theme.colors.background.bg4};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`
